/**
 * TRANSFER TARGETS — the desks a caller can be handed to, and the only numbers
 * this platform will dial because a model asked it to.
 *
 * ONE SCREEN, NOT TWO, unlike Tools and Webhooks. Those split into a list page
 * and a detail page because a row carries write-only credential headers, a JSON
 * Schema and a test harness — things that need room. A transfer target is five
 * fields and none of them are secret, so the list edits in place and there is no
 * second route to keep in sync.
 *
 * THE NUMBER IS SHOWN IN FULL, deliberately, and it is the one place in this
 * panel where a sensitive-looking value is not write-only. An operator cannot
 * check a desk number they are not allowed to see, and a wrong digit here means a
 * caller is connected to a stranger. What the number is kept away from is the
 * MODEL: the transfer tool offers it an enum of target NAMES and nothing else,
 * enforced server-side where the session config is built (see
 * packages/agent/src/transfers.ts). That boundary is not this screen's to keep.
 *
 * TWO THINGS THIS SCREEN HAS TO SAY OUT LOUD, because neither is guessable:
 *
 *   A NAME IS FOREVER. The model names desks, and a transferred session records
 *   which desk took it by name — so renaming would make history ambiguous and the
 *   server refuses it. Revoke and create a new one.
 *
 *   AN AGENT WITH NO TARGETS CANNOT TRANSFER AT ALL. The list of targets on an
 *   agent is the feature switch, not just a dropdown: empty means the model is
 *   given no transfer tool. Disabling the last enabled target of an agent does
 *   the same thing, from here.
 */
import { useEffect, useState } from 'react'
import {
  transferTargets,
  type CreateTransferTargetBody,
  type PatchTransferTargetBody,
  type TransferTargetView,
} from '@/api'
import { can } from '@/lib/auth'
import { useAuth } from '@/components/auth-provider'
import { DataTable, PageHeader, type Column } from '@/components/data-table'
import { fmtWhen } from '@/components/state-pill'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/**
 * The server's rules, restated for the client so a typo is caught before a round
 * trip — never INSTEAD of the round trip. `validateTransferTarget` in
 * @voiceai/agent is the authority and runs twice more (on write, and again at
 * hydration); this is a courtesy, and the server's message wins every
 * disagreement.
 */
const E164 = /^\+[1-9]\d{7,14}$/
const MIN_RING = 5
const MAX_RING = 120

function messageFor(e: unknown): string {
  if (e instanceof Error && e.message) return e.message
  return 'That did not work. Try again, or check the console.'
}

export function TransfersPage() {
  const { user } = useAuth()
  const canWrite = can(user, 'write')

  const [rows, setRows] = useState<TransferTargetView[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  /** Which row is open for editing, or null. One at a time: two open forms on a
   *  table of desks invites saving the wrong one. */
  const [editing, setEditing] = useState<string | null>(null)

  const reload = () => {
    const ac = new AbortController()
    transferTargets
      .list(ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return
        setRows(res.rows)
        setLoadError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setLoadError(messageFor(e))
      })
    return () => ac.abort()
  }
  useEffect(reload, [])

  // ------------------------------------------------------------------ create
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [destination, setDestination] = useState('')
  const [whisper, setWhisper] = useState('')
  const [ringSeconds, setRingSeconds] = useState('25')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const resetCreate = () => {
    setName('')
    setDescription('')
    setDestination('')
    setWhisper('')
    setRingSeconds('25')
    setCreateError(null)
  }

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (creating) return
    const dest = destination.trim()
    if (!E164.test(dest)) {
      setCreateError(
        'The destination must be a plain E.164 number — a "+" then 8 to 15 digits, with no '
          + 'spaces, dashes or brackets.',
      )
      return
    }
    const ring = Number(ringSeconds)
    if (!Number.isInteger(ring) || ring < MIN_RING || ring > MAX_RING) {
      setCreateError(`Ring time must be a whole number of seconds between ${MIN_RING} and ${MAX_RING}.`)
      return
    }
    const body: CreateTransferTargetBody = {
      name: name.trim(),
      description: description.trim(),
      destination: dest,
      ringSeconds: ring,
    }
    // Blank means "use the model's own sentence about this caller", which is the
    // normal case and the point of the feature. Sent as null rather than omitted
    // so the intent is explicit in the request.
    body.whisper = whisper.trim() === '' ? null : whisper.trim()
    setCreating(true)
    setCreateError(null)
    transferTargets
      .create(body, AbortSignal.timeout(20_000))
      .then(() => {
        setCreating(false)
        setShowCreate(false)
        resetCreate()
        reload()
      })
      .catch((err: unknown) => {
        setCreating(false)
        setCreateError(messageFor(err))
      })
  }

  // ------------------------------------------------------------------- table
  const columns: Column<TransferTargetView>[] = [
    {
      key: 'name',
      header: 'Desk',
      width: '14rem',
      cell: (t) => (
        <span className="flex items-center gap-1.5">
          <span className="truncate font-medium" title={t.name}>
            {t.name}
          </span>
          {!t.enabled && (
            <span
              className="pill pill-plain shrink-0"
              title={
                'Disabled: agents keep the reference, but no call will be transferred here. '
                + 'If this is an agent’s only target, that agent has no transfer tool at all.'
              }
            >
              disabled
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'destination',
      header: 'Dials',
      width: '11rem',
      // IN FULL. See the file header: an operator has to be able to check it.
      cell: (t) => <span className="mono block truncate text-xs">{t.destination}</span>,
    },
    {
      key: 'description',
      header: 'When the model picks it',
      cell: (t) => (
        <span className="block truncate text-sm text-muted-foreground" title={t.description}>
          {t.description}
        </span>
      ),
    },
    {
      key: 'whisper',
      header: 'Whisper',
      width: '8rem',
      hideBelow: 'md',
      cell: (t) => (
        <span
          className="text-sm text-muted-foreground"
          title={
            t.whisper === null
              ? 'The model writes one sentence about this caller and the answering human hears it.'
              : t.whisper
          }
        >
          {t.whisper === null ? 'from the model' : 'fixed script'}
        </span>
      ),
    },
    {
      key: 'ringSeconds',
      header: 'Rings',
      width: '5rem',
      hideBelow: 'lg',
      cell: (t) => (
        <span
          className="whitespace-nowrap text-sm"
          title="How long the desk rings before the caller is put back to the AI."
        >
          {t.ringSeconds}s
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      width: '11rem',
      hideBelow: 'lg',
      cell: (t) => <span className="whitespace-nowrap">{fmtWhen(t.createdAt)}</span>,
    },
  ]

  return (
    <div className="min-w-0">
      <PageHeader
        title="Transfer targets"
        subtitle={
          <>
            The people a phone caller can be handed to. An agent lists the desks it may reach,
            the model picks one <em>by name</em>, and the carrier dials the number &mdash; the
            number itself is never shown to the model. Phone calls only: a web caller has no
            carrier leg to hand over.
          </>
        }
        actions={
          canWrite && (
            <Button
              size="sm"
              className="h-8"
              onClick={() => {
                setShowCreate((v) => !v)
                setEditing(null)
              }}
            >
              {showCreate ? 'Close the form' : 'New target'}
            </Button>
          )
        }
      />

      {canWrite && showCreate ? (
        <section className="panel">
          <h2>
            Add a target
            <span className="note">the name is permanent &mdash; past transfers are recorded under it</span>
          </h2>
          <form onSubmit={onCreate} className="body flex flex-col gap-4">
            <div className="grid2">
              <div className="field">
                <Label htmlFor="xtName">Desk name</Label>
                <Input
                  id="xtName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="HR"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  What the model chooses between. Spaces and capitals are fine &mdash; this is a
                  label read by a language model, not an identifier. <strong>Not renameable:</strong>{' '}
                  a transferred call records this name forever.
                </p>
              </div>
              <div className="field">
                <Label htmlFor="xtDest">Number to dial</Label>
                <Input
                  id="xtDest"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="mono"
                  placeholder="+15551234567"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  E.164 exactly: a &ldquo;+&rdquo; then 8 to 15 digits, no spaces or dashes. Refused
                  rather than tidied up, so what you see here is what gets dialled. SIP
                  destinations are not supported yet.
                </p>
              </div>
            </div>

            <div className="field">
              <Label htmlFor="xtDesc">When should the model pick this desk?</Label>
              <Input
                id="xtDesc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Pay, leave, and anything about someone’s employment."
                required
              />
              <p className="text-xs text-muted-foreground">
                Read by the model, and it is the only thing that makes a list of several desks
                usable &mdash; without it the choice is a coin toss. Required for that reason.
              </p>
            </div>

            <div className="grid2">
              <div className="field">
                <Label htmlFor="xtWhisper">Fixed whisper (optional)</Label>
                <Input
                  id="xtWhisper"
                  value={whisper}
                  onChange={(e) => setWhisper(e.target.value)}
                  placeholder="Leave blank — recommended"
                />
                <p className="text-xs text-muted-foreground">
                  What the answering human hears before the caller is connected. Leave this blank
                  and the model writes one sentence about <em>this</em> caller
                  (&ldquo;asking about invoice 4471&rdquo;), which is the whole point. Fill it in
                  only when a desk must hear the same script every time.
                </p>
              </div>
              <div className="field">
                <Label htmlFor="xtRing">Ring for (seconds)</Label>
                <Input
                  id="xtRing"
                  type="number"
                  min={MIN_RING}
                  max={MAX_RING}
                  value={ringSeconds}
                  onChange={(e) => setRingSeconds(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  If nobody answers in this long, the caller is told and put back to a fresh AI
                  session &mdash; which will not remember the conversation they just had. Keep it
                  short enough that a caller waits through it.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="submit"
                disabled={
                  creating
                  || name.trim() === ''
                  || description.trim() === ''
                  || destination.trim() === ''
                }
              >
                {creating ? 'Creating…' : 'Create target'}
              </Button>
              {createError && <p className="err text-sm">{createError}</p>}
            </div>
          </form>
        </section>
      ) : null}

      {!canWrite && (
        <p className="mb-4 text-sm text-muted-foreground">
          Read-only: adding and editing targets is a write, and this account is a{' '}
          <span className="mono">{user?.role}</span>.
        </p>
      )}

      {loadError && <p className="err mb-4">{loadError}</p>}

      <DataTable
        columns={columns}
        rows={rows}
        keyOf={(t) => t.id}
        fixed
        onRowClick={canWrite ? (t) => setEditing((cur) => (cur === t.id ? null : t.id)) : undefined}
        emptyTitle="No transfer targets"
        emptyBody={
          <>
            Nothing is defined yet, so no agent can hand a caller to a person. Agents work fine
            without this &mdash; a model with no targets is simply never offered a transfer.
          </>
        }
      />

      {editing !== null && rows !== null ? (
        <EditPanel
          row={rows.find((r) => r.id === editing) ?? null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
        />
      ) : null}
    </div>
  )
}

/**
 * The in-place editor for one target.
 *
 * NO NAME FIELD, and that absence is the feature: the server refuses a rename
 * because a transferred session records the desk by name, so offering the field
 * would be offering a 400. The note says so where somebody would look for it.
 */
function EditPanel({
  row,
  onClose,
  onSaved,
}: {
  row: TransferTargetView | null
  onClose: () => void
  onSaved: () => void
}) {
  const [description, setDescription] = useState(row?.description ?? '')
  const [destination, setDestination] = useState(row?.destination ?? '')
  const [whisper, setWhisper] = useState(row?.whisper ?? '')
  const [ringSeconds, setRingSeconds] = useState(String(row?.ringSeconds ?? 25))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState(false)

  if (row === null) return null

  const save = (patch: PatchTransferTargetBody) => {
    setSaving(true)
    setError(null)
    transferTargets
      .update(row.id, patch, AbortSignal.timeout(20_000))
      .then(() => {
        setSaving(false)
        onSaved()
      })
      .catch((e: unknown) => {
        setSaving(false)
        setError(messageFor(e))
      })
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (saving) return
    const dest = destination.trim()
    if (!E164.test(dest)) {
      setError('The destination must be a plain E.164 number: a "+" then 8 to 15 digits.')
      return
    }
    const ring = Number(ringSeconds)
    if (!Number.isInteger(ring) || ring < MIN_RING || ring > MAX_RING) {
      setError(`Ring time must be a whole number between ${MIN_RING} and ${MAX_RING}.`)
      return
    }
    save({
      description: description.trim(),
      destination: dest,
      whisper: whisper.trim() === '' ? null : whisper.trim(),
      ringSeconds: ring,
    })
  }

  return (
    <section className="panel mt-4">
      <h2>
        {row.name}
        <span className="note">
          the name is permanent &mdash; revoke and create a new desk to change it
        </span>
      </h2>
      <form onSubmit={onSubmit} className="body flex flex-col gap-4">
        <div className="grid2">
          <div className="field">
            <Label htmlFor="xtEditDest">Number to dial</Label>
            <Input
              id="xtEditDest"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="mono"
              autoCapitalize="none"
              spellCheck={false}
              required
            />
            <p className="text-xs text-muted-foreground">
              Changing this re-points every agent that reaches this desk, on their next call.
            </p>
          </div>
          <div className="field">
            <Label htmlFor="xtEditRing">Ring for (seconds)</Label>
            <Input
              id="xtEditRing"
              type="number"
              min={MIN_RING}
              max={MAX_RING}
              value={ringSeconds}
              onChange={(e) => setRingSeconds(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="field">
          <Label htmlFor="xtEditDesc">When should the model pick this desk?</Label>
          <Input
            id="xtEditDesc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </div>

        <div className="field">
          <Label htmlFor="xtEditWhisper">Fixed whisper</Label>
          <Input
            id="xtEditWhisper"
            value={whisper}
            onChange={(e) => setWhisper(e.target.value)}
            placeholder="Blank — the model writes one sentence per caller"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Close
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={saving}
            onClick={() => save({ enabled: !row.enabled })}
            title={
              row.enabled
                ? 'Stop routing transfers here. Reversible, and it does not burn the name. If this is an agent’s only target, that agent loses its transfer tool.'
                : 'Start routing transfers here again.'
            }
          >
            {row.enabled ? 'Disable' : 'Enable'}
          </Button>
          <span className="grow" />
          {confirmRevoke ? (
            <>
              <span className="text-sm text-muted-foreground">
                Revoke <span className="mono">{row.name}</span>? Permanent, and the name can
                never be reused.
              </span>
              <Button
                type="button"
                variant="destructive"
                disabled={saving}
                onClick={() => {
                  setSaving(true)
                  transferTargets
                    .revoke(row.id, AbortSignal.timeout(20_000))
                    .then(() => {
                      setSaving(false)
                      onSaved()
                    })
                    .catch((e: unknown) => {
                      setSaving(false)
                      setError(messageFor(e))
                    })
                }}
              >
                Revoke
              </Button>
              <Button type="button" variant="secondary" onClick={() => setConfirmRevoke(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="secondary"
              disabled={saving}
              onClick={() => setConfirmRevoke(true)}
              title="Permanent. Use Disable for a desk that is closed today and open tomorrow."
            >
              Revoke…
            </Button>
          )}
        </div>
        {error && <p className="err text-sm">{error}</p>}
      </form>
    </section>
  )
}
